/**
 * Trending Posts Module
 *
 * Fetches ready-to-render trending posts from Cloudflare Worker
 * and displays them in the trending section.
 */

/**
 * Escapes HTML entities to prevent XSS
 */
function escapeHtml(text) {
  if (!text) return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Sanitizes a URL to prevent XSS
 */
function sanitizeUrl(url) {
  if (!url) return "#";
  const urlPattern = /^(https?:\/\/|\/)/i;
  return urlPattern.test(url) ? escapeHtml(url) : "#";
}

/**
 * Renders a single trending article item with numbering
 */
function renderTrendingItem(post, index) {
  const title = escapeHtml(post.title);
  const url = sanitizeUrl(post.url);
  const primaryTag = post.primary_tag;

  const categoryStyle = primaryTag?.accent_color
    ? ` style="--category-color: ${primaryTag.accent_color}"`
    : "";
  const categoryHtml = primaryTag
    ? `<span class="article-teaser__category"${categoryStyle}>${escapeHtml(primaryTag.name)}</span>`
    : "";

  return `
    <article class="trending-item">
      <a href="${url}" class="trending-item__link">
        <span class="trending-item__number">${index + 1}.</span>
        <div class="trending-item__content">
          ${categoryHtml}
          <h3 class="trending-item__title">${title}</h3>
        </div>
      </a>
    </article>
  `;
}

/**
 * Fetches trending posts from worker and displays them
 */
async function fetchAndDisplayTrendingPosts(workerUrl, siteId, ghostApiKey) {
  const section = document.getElementById("trending-posts-section");
  const container = document.getElementById("trending-posts-container");

  if (!section || !container) return;

  try {
    const url = new URL(workerUrl);
    url.searchParams.set("site_id", siteId);
    url.searchParams.set("ghost_key", ghostApiKey);

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Worker error: ${response.status}`);
    }

    const data = await response.json();
    const posts = data.posts || [];

    if (!posts.length) {
      section.style.display = "none";
      return;
    }

    container.innerHTML = posts
      .map((post, index) => renderTrendingItem(post, index))
      .join("");
    section.style.display = "block";
  } catch (error) {
    console.error("Error fetching trending posts:", error);
    section.style.display = "none";
  }
}

/**
 * Initialize trending posts feature
 */
export function initTrendingPosts() {
  const container = document.getElementById("trending-posts-container");
  if (!container) return;

  const body = document.body;
  const workerUrl = body.dataset.trendingWorkerUrl;
  const siteId = body.dataset.plausibleDomain;
  const ghostApiKey = body.dataset.ghostApiKey;

  if (!workerUrl || !siteId || !ghostApiKey) {
    return;
  }

  fetchAndDisplayTrendingPosts(workerUrl, siteId, ghostApiKey);
}
